import { useState } from 'react'
import type { ReactElement } from 'react'
import { BlockPicker } from '../components/BlockPicker'
import { AppRoot } from '../components/AppRoot'
import { FourWeekConfirm } from '../components/FourWeekConfirm'
import type { BlockCode, WeekStart } from '../engine/types'
import { clearLocalProfile, readLocalProfile } from '../lib/local-profile'
import type { RosterType } from '../lib/profile'
import { updateProfile } from '../lib/profile'
import { useSession } from '../lib/session-context'
import { todayIso } from '../lib/today'
import { Button, SegmentedControl, TextField } from '../ui'
import type { SegmentedOption } from '../ui'

type Step = 'name' | 'roster-type' | 'block' | 'confirm' | 'details'

const ROSTER_TYPE_OPTIONS: SegmentedOption<RosterType>[] = [
  { value: 'BLOCK', label: 'Block roster' },
  { value: 'FWA', label: 'FWA' },
  { value: 'BLOCK_PARTIAL', label: 'Partial block roster' },
]

/**
 * `/` when `decideGate` (`../lib/gate`) returns `'SETUP'` — an `ACTIVE` profile missing
 * something `isProfileComplete` requires. Takes no props at all: unlike `SignInScreen`'s
 * `onSignedIn` or `JoinScreen`'s `onRedeemed`, there is nothing for this screen to call back to.
 * It is rendered by `AppRoutes` at the exact same URL `props.shell` (`'READY'`) renders at, so
 * the moment `handleFinish` below saves successfully and calls `refresh()`, the freshly-fetched
 * profile flips `decideGate`'s verdict to `'READY'` on `AppRoutes`'s own next render and this
 * screen is swapped out from above — no navigation, no completion callback, nothing else to do.
 *
 * Five steps in local `useState`, matching `../components/Onboarding.tsx`'s pattern (which this
 * screen absorbs and generalizes): `'name'` → `'roster-type'` → (`'block'` → `'confirm'`, skipped
 * entirely for `'FWA'`) → `'details'`. Every step renders inside the one `<AppRoot>` wrapping the
 * whole screen, never one per step.
 *
 * **Pre-fill precedence.** `SETUP` means the `profiles` row already exists — an admin may have
 * called `admin_set_block` before this screen ever rendered, or the user may be back here a
 * second time after a partial fill. `profile` (from `useSession()`) is always the primary source
 * for a field's initial value; `readLocalProfile()` (Phase 1's migration source, read once via a
 * `useState` initializer) only fills `blockCode`/`weekStart` when the server-side field is null.
 * `weekStart` is not a step here — it's a `SettingsScreen` preference — so the precedence rule's
 * result for it is carried silently into the final save with no UI asking about it.
 */
export function SetupScreen(): ReactElement {
  const { profile, refresh } = useSession()
  // `SetupScreen` is only ever rendered once `decideGate` has returned 'SETUP', which requires
  // profile !== null as its own precondition (gate.ts) — there is no reachable null-profile case
  // here. A single assertion trusts that invariant instead of adding a branch (and an `| null`
  // return type) this screen can never actually take.
  const activeProfile = profile as NonNullable<typeof profile>

  const [localProfile] = useState(() => readLocalProfile())

  const [step, setStep] = useState<Step>('name')
  const [today] = useState(() => todayIso())

  const [displayName, setDisplayName] = useState(() => activeProfile.display_name ?? '')
  const [rosterType, setRosterType] = useState<RosterType>(
    () => activeProfile.roster_type ?? 'BLOCK',
  )
  const [blockCode, setBlockCode] = useState<BlockCode | null>(
    () => activeProfile.block_code ?? localProfile?.blockCode ?? null,
  )
  // Carried straight into the final save with no UI step of its own — see the docstring above.
  const [weekStart] = useState<WeekStart>(
    () => activeProfile.week_start ?? localProfile?.weekStart ?? 'SUNDAY',
  )
  const [classification, setClassification] = useState(() => activeProfile.classification ?? '')
  const [station, setStation] = useState(() => activeProfile.station ?? '')
  const [facebookUsername, setFacebookUsername] = useState(
    () => activeProfile.facebook_username ?? '',
  )

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  async function handleFinish() {
    setSaving(true)
    setSaveError(null)

    const { error } = await updateProfile(activeProfile.id, {
      display_name: displayName.trim(),
      roster_type: rosterType,
      block_code: rosterType === 'FWA' ? null : blockCode,
      week_start: weekStart,
      classification: classification.trim() || null,
      station: station.trim() || null,
      facebook_username: facebookUsername.trim() || null,
    })

    if (error) {
      setSaveError(error.message)
      setSaving(false)
      return
    }

    // Only cleared once the save has actually succeeded — an early clear would strand a user
    // who hits a save error with no migrated block to fall back on if they reload mid-retry.
    clearLocalProfile()
    await refresh()
  }

  return (
    <AppRoot>
      {step === 'name' && (
        <div className="sl-stack">
          <div>
            <h1 className="sl-heading">What's your name?</h1>
            <p className="sl-hint">This is how other paramedics will see you on swaps.</p>
          </div>
          <TextField label="Display name" value={displayName} onChange={setDisplayName} />
          <Button
            variant="primary"
            block
            disabled={displayName.trim() === ''}
            onClick={() => setStep('roster-type')}
          >
            Continue
          </Button>
        </div>
      )}

      {step === 'roster-type' && (
        <div className="sl-stack">
          <div>
            <h1 className="sl-heading">What's your roster type?</h1>
            <p className="sl-hint">This decides how the app works out your shifts.</p>
          </div>
          <SegmentedControl
            label="Roster type"
            options={ROSTER_TYPE_OPTIONS}
            value={rosterType}
            onChange={setRosterType}
          />
          <Button
            variant="primary"
            block
            onClick={() => setStep(rosterType === 'FWA' ? 'details' : 'block')}
          >
            Continue
          </Button>
          <Button variant="ghost" block onClick={() => setStep('name')}>
            Back
          </Button>
        </div>
      )}

      {(step === 'block' || (step === 'confirm' && blockCode === null)) && (
        <div className="sl-stack">
          <div>
            <h1 className="sl-heading">Which block are you on?</h1>
            <p className="sl-hint">
              Pick your roster block — everything else in the app works out from it.
            </p>
          </div>
          <BlockPicker label="Your roster block" value={blockCode} onChange={setBlockCode} />
          <Button
            variant="primary"
            block
            disabled={blockCode === null}
            onClick={() => setStep('confirm')}
          >
            Continue
          </Button>
          <Button variant="ghost" block onClick={() => setStep('roster-type')}>
            Back
          </Button>
        </div>
      )}

      {step === 'confirm' && blockCode !== null && (
        <FourWeekConfirm
          block={blockCode}
          today={today}
          onConfirm={() => setStep('details')}
          onBack={() => setStep('block')}
        />
      )}

      {step === 'details' && (
        <div className="sl-stack">
          <div>
            <h1 className="sl-heading">A few more details</h1>
            <p className="sl-hint">
              Optional — you can fill these in now or add them later in Settings.
            </p>
          </div>
          <TextField
            label="Classification"
            value={classification}
            onChange={setClassification}
            disabled={saving}
          />
          <TextField label="Station" value={station} onChange={setStation} disabled={saving} />
          <TextField
            label="Messenger username"
            value={facebookUsername}
            onChange={setFacebookUsername}
            disabled={saving}
            hint="Find yours at facebook.com/your.name, or in your profile's URL. Optional — you can add this later in Settings."
          />
          {saveError ? (
            <p className="sl-hint" role="alert">
              {saveError}
            </p>
          ) : null}
          <Button variant="primary" block disabled={saving} onClick={handleFinish}>
            Finish setup
          </Button>
          <Button
            variant="ghost"
            block
            disabled={saving}
            onClick={() => setStep(rosterType === 'FWA' ? 'roster-type' : 'confirm')}
          >
            Back
          </Button>
        </div>
      )}
    </AppRoot>
  )
}
